import React, { useMemo } from 'react';
import { Box, Typography } from '@/shared/design-system/primitives';
import { SimulationResultPoint } from '@/shared/logic/metabolic/v5/types';
import { SystemicPhaseResolver } from '@/shared/logic/metabolic/v5/resolvers/systemic-phase-resolver';

interface SystemicPhaseTimelineProps {
    simulation: SimulationResultPoint[];
}

/**
 * SystemicPhaseTimeline: Visualizes the fluid 0-100 metabolic cycle.
 * FIXED: Strictly clamps rendering to the 0-100% visible window.
 */
export const SystemicPhaseTimeline: React.FC<SystemicPhaseTimelineProps> = ({ simulation }) => {
    if (!simulation || simulation.length === 0) return null;

    const numericStyle = { fontVariantNumeric: 'lining-nums', fontFamily: 'ui-sans-serif, system-ui, sans-serif' };

    // 1. DATA PROCESSING
    const phaseData = useMemo(() => {
        let lastHighInsulinMin = -1440;
        
        // Use full simulation to find the starting fasting state (context)
        simulation.forEach(p => {
            if (p.minute < 0 && p.state.insulin > 10.0) lastHighInsulinMin = p.minute;
        });

        const results: any[] = [];
        const transitions: any[] = [];
        let lastPhase = "";

        // CLAMP: Only plot today (0 to 100 relative X)
        const visiblePoints = simulation.filter(p => p.x >= 0 && p.x <= 100);

        visiblePoints.forEach((p, i) => {
            if (p.state.insulin > 10.0) lastHighInsulinMin = p.minute;
            const hoursFasted = Math.max(0, (p.minute - lastHighInsulinMin) / 60);
            const res = SystemicPhaseResolver.compute(p.state.insulin, p.state.glycogenDebt, hoursFasted);

            results.push({ ...p, ...res });

            if (res.label !== lastPhase) {
                const hour = Math.floor(p.minute / 60);
                const min = Math.floor(p.minute % 60);
                const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
                transitions.push({ time: timeStr, label: res.label, color: res.color, x: p.x });
                lastPhase = res.label;
            }
        });

        return { points: results, transitions };
    }, [simulation]);

    const scaleY = (score: number) => 98 - (score * 0.98);

    const path = useMemo(() => {
        if (phaseData.points.length === 0) return "";
        return phaseData.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${scaleY(p.score)}`).join(' ');
    }, [phaseData]);

    const PHASES = [
        { label: 'STORAGE', color: '#ef4444', y: '85%' },
        { label: 'REPAIR', color: '#2ecc71', y: '65%' },
        { label: 'DRAIN', color: '#eab308', y: '45%' },
        { label: 'FAT BURN', color: '#f97316', y: '25%' },
        { label: 'KETO', color: '#a855f7', y: '12%' },
        { label: 'RECYCLE', color: '#3b82f6', y: '2%' }
    ];

    return (
        <Box style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)', paddingTop: 20 }}>
            <Typography style={{ fontSize: 11, fontWeight: '950', color: '#fff', letterSpacing: 1.5, marginBottom: 20, textTransform: 'uppercase' }}>
                SYSTEMIC PHASE TIMELINE
            </Typography>

            <Box style={{ height: 150, width: '100%', position: 'relative' }}>
                {/* ZONE BACKGROUNDS */}
                <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'column-reverse' }}>
                    <Box style={{ flex: 20, backgroundColor: '#ef4444', opacity: 0.15 }} />
                    <Box style={{ flex: 20, backgroundColor: '#2ecc71', opacity: 0.15 }} />
                    <Box style={{ flex: 20, backgroundColor: '#eab308', opacity: 0.15 }} />
                    <Box style={{ flex: 20, backgroundColor: '#f97316', opacity: 0.15 }} />
                    <Box style={{ flex: 10, backgroundColor: '#a855f7', opacity: 0.15 }} />
                    <Box style={{ flex: 10, backgroundColor: '#3b82f6', opacity: 0.15 }} />
                </Box>

                {/* PHASE LABELS */}
                <Box style={{ position: 'absolute', left: 10, top: 0, bottom: 0, pointerEvents: 'none' } as any}>
                    {PHASES.map(p => (
                        <Typography key={p.label} style={{ position: 'absolute', top: p.y, fontSize: 8, fontWeight: '900', color: p.color, opacity: 0.6, letterSpacing: 1 }}>
                            {p.label}
                        </Typography>
                    ))}
                </Box>

                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: 'hidden' }}>
                    {[0.25, 0.5, 0.75].map(tick => (
                        <line key={tick} x1="0" y1={98 - tick * 98} x2="100" y2={98 - tick * 98} stroke="#333" strokeWidth="0.5" strokeDasharray="2,2" opacity={0.3} />
                    ))}
                    <line x1="0" y1="98" x2="100" y2="98" stroke="#333" strokeWidth="1" vectorEffect="non-scaling-stroke" />

                    {/* Transition Markers */}
                    {phaseData.transitions.map((t, i) => (
                        <g key={i}>
                            <line x1={t.x} y1="0" x2={t.x} y2="100" stroke={t.color} strokeWidth={0.5} strokeDasharray="2,2" opacity={0.4} vectorEffect="non-scaling-stroke" />
                        </g>
                    ))}

                    <path d={path} fill="none" stroke="#fff" strokeWidth={1.5} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                </svg>
            </Box>

            <Box style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 5, marginTop: 10 }}>
                {['00', '06', '12', '18', '24'].map(h => (
                    <Typography key={h} style={{ fontSize: 11, color: '#666', fontWeight: '900', ...numericStyle }}>{h}</Typography>
                ))}
            </Box>

            {/* TRANSITION LOG */}
            <Box style={{ marginTop: 20 }}>
                {phaseData.transitions.map((t, i) => (
                    <Box key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                        <Box style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Box style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: t.color, marginRight: 10 }} />
                            <Typography style={{ fontSize: 10, fontWeight: '900', color: t.color, textTransform: 'uppercase', letterSpacing: 1 }}>{t.label}</Typography>
                        </Box>
                        <Typography style={{ fontSize: 10, fontWeight: '900', color: '#888', ...numericStyle }}>{t.time}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
